import type { ReactNode } from 'react'
import { View } from 'react-native'

import type { TPrayMutationData } from '@/src/features/pray/api/prayApi.types'
import { PrayScreen } from '@/src/features/pray/ui/screens/PrayScreen'
import { TEST_ID } from '@/src/general/constants/testId'
import type { TRACKING_SOURCE } from '@/src/general/utils/gtm/constants/TrackingSource.enum'
import { useTranslate } from '@/src/lang/useTranslate'

import { INTENTION_TYPE } from '../../../models/Intention.enums'
import type { TTypeIntention } from '../../../models/Intention.types'
import { useIntentionPrayMutation } from '../../../services/useIntentionPrayMutation'
import { getIntentionUserName } from '../../../utils/getIntentionUserName'
import { BirthdayContent } from './BirthdayContent'
import { IntentionContent } from './IntentionContent'

const intentionIsOfType = <Type extends INTENTION_TYPE>(
  type: Type,
  intention?: TTypeIntention,
): intention is TTypeIntention<Type> => !intention || intention.type === type

export type TIntentionPrayProps = {
  intention?: TTypeIntention
  type: INTENTION_TYPE
  trackingSource: TRACKING_SOURCE
  onAmen: (prayer?: TPrayMutationData) => void
  onClose: VoidFunction
  theme?: string
}

export const IntentionPray = ({
  intention,
  type,
  trackingSource,
  onAmen,
  onClose,
  theme,
}: TIntentionPrayProps): ReactNode => {
  const { t } = useTranslate()

  const { data: prayer, mutate: pray, isPending, isSuccess } = useIntentionPrayMutation()

  const handlePray = () => {
    if (intention && !isPending && !isSuccess) pray({ ...intention, landingPageSlug: theme })
  }

  const getTitleForIntentionType = () => {
    if (!intention) return undefined

    const userName = getIntentionUserName(intention, t('intention.anonymous-praying'))

    switch (type) {
      case INTENTION_TYPE.INTENTION:
        return t('pray.intention.title', { userName })
      case INTENTION_TYPE.ARRIVAL:
        return t('pray.intention.arrival.title', { userName })
      case INTENTION_TYPE.BIRTHDAY:
        return t('pray.intention.birthday.title', { userName })
      default:
        return t('pray.intention.title', { userName })
    }
  }

  return (
    <PrayScreen
      testID={TEST_ID.PRAY.SCREEN.NAME}
      trackingSource={trackingSource}
      onPray={handlePray}
      onAmen={() => onAmen(prayer)}
      onClose={onClose}
      title={getTitleForIntentionType()}
      isLoading={!intention}
    >
      <View className="mx-auto max-w-screen-md justify-center p-4">
        {type === INTENTION_TYPE.INTENTION && intentionIsOfType(INTENTION_TYPE.INTENTION, intention) && (
          <IntentionContent intention={intention} />
        )}
        {type === INTENTION_TYPE.BIRTHDAY && intentionIsOfType(INTENTION_TYPE.BIRTHDAY, intention) && (
          <BirthdayContent intention={intention} />
        )}
      </View>
    </PrayScreen>
  )
}
